/**
 * Sprachprinzip Flex4Success v8 (verbindlich):
 * 1. Alltagssprache, keine Anglizismen-Übersetzungen
 * 2. Metaphern aus Andreas' Coaching-Modell (Sanduhr, Radar, Tür/Schlüssel, Standortbestimmung)
 * 3. Psychologisch fundiert, nicht esoterisch
 * 4. Kein Coach-Sprech
 * 5. Werkzeug-Namen in einem Satz erklärbar
 * 6. 'Primärfokustyp' statt 'Fokustyp' im Singular-Kontext
 *
 * Sprint M5.23 (29.07.2026) — M-11 Meine Primärfokustyp-Analyse.
 *
 * 20-Fragen-Wizard mit Auto-Save nach jeder Antwort. Frank ersetzt Copy in
 * Phase 5 (Fragen-Texte, Ergebnis-Deutung). Die Analyse basiert auf einer
 * regelbasierten Heuristik.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, CheckCircle2, Info } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { api } from "@/lib/api";
import { CockpitWord } from "@/lib/journeyFlags";

const NAMES = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };
const COLORS = { eule: "#4563BB", biene: "#F59E0B", katze: "#7C3AED", hund: "#059669" };

// Issue 29 (04.09.2026): Smileys statt reiner 1-5 Skala
const SCALE = [
  { value: 1, label: "gar nicht", emoji: "😞" },
  { value: 2, label: "eher nicht", emoji: "🙁" },
  { value: 3, label: "neutral", emoji: "😐" },
  { value: 4, label: "eher zu", emoji: "🙂" },
  { value: 5, label: "voll zu", emoji: "😀" },
];

function ProgressBar({ current, total }) {
  const pct = total ? Math.round((current / total) * 100) : 0;
  return (
    <div className="w-full" data-testid="pro-analysis-progress">
      <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1.5">
        <span>Frage {Math.min(current + 1, total)} von {total}</span>
        <span>{pct} %</span>
      </div>
      <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full bg-[#4563BB] transition-all duration-300"
          style={{ width: `${pct}%` }}
          data-testid="pro-analysis-progress-fill"
        />
      </div>
    </div>
  );
}

function ResultBar({ typeKey, percent }) {
  return (
    <div className="flex items-center gap-3" data-testid={`pro-analysis-bar-${typeKey}`}>
      <div className="w-20 text-sm font-bold text-slate-800 dark:text-slate-100">{NAMES[typeKey]}</div>
      <div className="flex-1 h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${percent}%`, backgroundColor: COLORS[typeKey] }}
        />
      </div>
      <div className="w-12 text-right text-sm font-mono text-slate-600 dark:text-slate-300">{percent.toFixed(0)}%</div>
    </div>
  );
}

export default function ProAnalysisWizard() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | intro | wizard | result | forbidden
  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Sprint M5.36 (29.07.2026) — Issue 12 (P0): Free-User duerfen den M-11
  // Wizard (20 Fragen) NIE sehen, auch nicht via Deep-Link. Wir laden zuerst
  // /auth/me und redirecten Free strikt auf /dashboard/test (M-10 Quick-Check).
  // Setzt `authChecked=true`, sobald User-Tier bekannt ist — der 2. useEffect
  // wartet darauf und feuert die Pro-Fetches erst dann (vermeidet 403-Noise).
  const [authChecked, setAuthChecked] = useState(false);
  const [isProAllowed, setIsProAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (cancelled) return;
        const tier = (me?.data?.license_tier || me?.data?.tier || "free").toLowerCase();
        const isPro = tier.startsWith("pro_solo") || tier === "master" || tier === "pro";
        if (!isPro) {
          navigate("/dashboard/test", { replace: true });
          return;
        }
        setIsProAllowed(true);
        setAuthChecked(true);
      } catch (_e) {
        // Nicht eingeloggt → Login-Flow greift woanders. Hier keine harte Weiterleitung.
        setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Initial-Load: Fragen + evtl. Resume — nur wenn Pro-User erkannt.
  useEffect(() => {
    if (!authChecked || !isProAllowed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const qResp = await api.get("/pro/analysis/questions");
        if (cancelled) return;
        const qs = qResp.data?.questions || [];
        setQuestions(qs);

        const resumeResp = await api.get("/pro/analysis/resume").catch((err) => {
          if (err?.response?.status === 403) {
            setPhase("forbidden");
          }
          return null;
        });
        if (cancelled) return;
        const s = resumeResp?.data?.session;
        if (s) {
          setSessionId(s.session_id);
          setAnswers(s.answers || {});
          if (s.completed && s.result) {
            setResult(s.result);
            setPhase("result");
          } else {
            const answered = Object.keys(s.answers || {}).length;
            setCurrent(Math.min(answered, qs.length - 1));
            setPhase(answered > 0 ? "wizard" : "intro");
          }
        } else {
          setPhase("intro");
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          setPhase("forbidden");
        } else {
          setPhase("intro");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authChecked, isProAllowed]);

  async function handleStart() {
    setErrorMsg(null);
    try {
      const r = await api.post("/pro/analysis/start", {
        resume_session_id: sessionId || undefined,
      });
      setSessionId(r.data.session_id);
      setAnswers(r.data.answers || {});
      if (r.data.completed && r.data.result) {
        setResult(r.data.result);
        setPhase("result");
      } else {
        const answered = Object.keys(r.data.answers || {}).length;
        setCurrent(answered);
        setPhase("wizard");
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        setPhase("forbidden");
      } else {
        setErrorMsg("Wir konnten die Analyse nicht starten. Versuch's gleich noch einmal.");
      }
    }
  }

  async function submitAnswer(value) {
    if (!sessionId || saving) return;
    const q = questions[current];
    if (!q) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const r = await api.post("/pro/analysis/answer", {
        session_id: sessionId,
        question_id: q.id,
        answer: value,
      });
      setAnswers(r.data.answers || {});
      if (r.data.completed && r.data.result) {
        setResult(r.data.result);
        setPhase("result");
      } else if (current < questions.length - 1) {
        setCurrent(current + 1);
      }
    } catch (err) {
      setErrorMsg("Antwort wurde nicht gespeichert. Bitte klick sie noch einmal an.");
    } finally {
      setSaving(false);
    }
  }

  function handleBack() {
    if (current > 0) setCurrent(current - 1);
  }

  const currentQuestion = questions[current];
  const answeredCurrent = currentQuestion ? answers[currentQuestion.id] : null;

  return (
    <RedesignLayout>
      {() => (
        <div className="max-w-2xl mx-auto px-4 py-10" data-testid="pro-analysis-page">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-[#4563BB] mb-6"
            data-testid="pro-analysis-back"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zum Mitgliederbereich
          </button>

          <header className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]" data-testid="pro-analysis-eyebrow">
              M-11 · Pro-Modul
            </p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="pro-analysis-headline">
              Meine Primärfokustyp-Analyse
            </h1>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              20 Fragen für die präzise Bestätigung deines Primärfokustyps.
              Jede Antwort wird automatisch gespeichert.
            </p>
          </header>

          {phase === "loading" && (
            <div className="flex justify-center py-16" data-testid="pro-analysis-loading">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}

          {phase === "forbidden" && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="pro-analysis-forbidden">
              <Sparkles className="w-8 h-8 text-amber-600 mx-auto" />
              <h2 className="mt-3 text-lg font-bold text-amber-900">Nur für Pro-Mitglieder.</h2>
              <p className="mt-2 text-sm text-amber-900">
                Diese Analyse ist Teil des Monats- und Jahresplans.
              </p>
              <button
                type="button"
                onClick={() => navigate("/pricing")}
                className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold px-4 py-2 text-sm"
                data-testid="pro-analysis-upgrade-cta"
              >
                Mehr erfahren
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {phase === "intro" && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pro-analysis-intro">
              <h2 className="text-xl font-extrabold text-slate-900">So läuft die Analyse ab.</h2>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>20 Aussagen — für jede wählst du eine Skala von 1 (gar nicht) bis 5 (voll zu).</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Auto-Save nach jeder Antwort — komm jederzeit zurück und mach weiter.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Am Ende: dein Primärfokustyp mit Konfidenz-Wert und Balken pro Typ.</span>
                </li>
              </ul>
              <div className="mt-5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3 flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300" data-testid="pro-analysis-disclaimer">
                <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span>
                  Alle Fragen beruhen auf Andreas' Fokustyp-Modell. Deine Antworten werden nach jeder Frage gespeichert — du kannst jederzeit unterbrechen und später weitermachen.
                </span>
              </div>
              <button
                type="button"
                onClick={handleStart}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4563BB] hover:bg-[#3a52a3] text-white font-bold px-4 py-3"
                data-testid="pro-analysis-start-btn"
              >
                {Object.keys(answers).length > 0 ? "Weitermachen" : "Analyse starten"}
                <ArrowRight className="w-4 h-4" />
              </button>
              {errorMsg && <p className="mt-3 text-sm text-red-600">{errorMsg}</p>}
            </div>
          )}

          {phase === "wizard" && currentQuestion && (
            <div data-testid="pro-analysis-wizard">
              <ProgressBar current={current} total={questions.length} />
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion.id}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="mt-6 rounded-2xl border border-slate-200 bg-white p-6"
                >
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    Frage {current + 1}
                  </p>
                  <p className="mt-2 text-lg sm:text-xl font-extrabold text-slate-900 leading-snug" data-testid="pro-analysis-question-text">
                    {currentQuestion.text}
                  </p>
                  <div className="mt-6 grid grid-cols-5 gap-2" data-testid="pro-analysis-scale">
                    {SCALE.map((s) => {
                      const isSel = answeredCurrent === s.value;
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => submitAnswer(s.value)}
                          disabled={saving}
                          className={`rounded-xl border p-3 text-center transition-colors ${
                            isSel
                              ? "border-[#4563BB] bg-[#4563BB] text-white"
                              : "border-slate-200 bg-white text-slate-700 hover:border-[#4563BB]"
                          } ${saving ? "opacity-60 cursor-wait" : ""}`}
                          data-testid={`pro-analysis-answer-${s.value}`}
                        >
                          <span className="block text-2xl sm:text-3xl leading-none" aria-hidden="true">{s.emoji}</span>
                          <span className="block text-[10px] uppercase tracking-wider mt-1">{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={current === 0}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="pro-analysis-prev-btn"
                    >
                      <ArrowLeft className="w-4 h-4" /> Vorherige
                    </button>
                    <span className="text-xs text-slate-400">Auto-Save aktiv</span>
                  </div>
                </motion.div>
              </AnimatePresence>
              {errorMsg && <p className="mt-3 text-sm text-red-600">{errorMsg}</p>}
              <p className="mt-4 text-xs text-slate-400 text-center" data-testid="pro-analysis-wizard-disclaimer">
                Du kannst jederzeit unterbrechen. Deine Antworten sind gespeichert.
              </p>
            </div>
          )}

          {phase === "result" && result && (
            <ResultView result={result} onRestart={() => {
              setSessionId(null);
              setAnswers({});
              setCurrent(0);
              setResult(null);
              setPhase("intro");
            }} />
          )}
        </div>
      )}
    </RedesignLayout>
  );
}

function ResultView({ result, onRestart }) {
  const navigate = useNavigate();
  const primary = result.primary_fokustyp;
  const secondary = result.secondary_fokustyp;
  const percent = result.percent_by_type || {};
  const confidencePct = Math.round((result.confidence || 0) * 100);

  // Issue 32: Normalisiere die 4 Ergebnisbalken auf 100% Summe, damit User
  // nicht durch nicht-additive Roh-Werte verwirrt wird.
  const rawSum = ["katze", "eule", "biene", "hund"]
    .reduce((s, k) => s + Number(percent[k] || 0), 0) || 1;
  const normalized = Object.fromEntries(
    ["katze", "eule", "biene", "hund"].map((k) => [k, (Number(percent[k] || 0) / rawSum) * 100])
  );

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8" data-testid="pro-analysis-result">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Analyse abgeschlossen
        </div>
        <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white" data-testid="pro-analysis-result-primary">
          Dein Primärfokustyp: {NAMES[primary] || primary}
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Ausprägung: <strong data-testid="pro-analysis-result-confidence">{confidencePct}%</strong>
          {secondary && (
            <>
              {" · "}Zweit-Tendenz: <strong data-testid="pro-analysis-result-secondary">{NAMES[secondary] || secondary}</strong>
            </>
          )}
        </p>
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          Ausprägung = wie deutlich dein Primärtyp gegenüber den anderen dreien überwiegt (0–100 %).
        </p>
      </div>

      <div className="mt-8 space-y-3">
        {["katze", "eule", "biene", "hund"].map((k) => (
          <ResultBar key={k} typeKey={k} percent={normalized[k]} />
        ))}
      </div>

      {/* Issue 34 — Klare Button-Hierarchie: Primärer Steckbrief-Btn oben (was der User als nächstes braucht) */}
      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={() => navigate(`/dashboard/fokustyp/${primary}`)}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#4563BB] hover:bg-[#3a52a3] text-white font-bold px-5 py-3 text-base"
          data-testid="pro-analysis-result-primary-detail-btn"
        >
          Steckbrief {NAMES[primary] || primary} öffnen
          <ArrowRight className="w-4 h-4" />
        </button>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-bold px-4 py-2 text-sm hover:border-slate-400"
            data-testid="pro-analysis-result-back-dashboard-btn"
            title="Bringt dich zurück zur Startseite mit allen Modulen"
          >
            Zurück zum Mitgliederbereich
          </button>
          <button
            type="button"
            onClick={() => navigate("/andere-verstehen")}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-bold px-4 py-2 text-sm hover:border-slate-400"
            data-testid="pro-analysis-result-andere-btn"
            title="Öffnet die vier Fokustyp-Steckbriefe zur Erkundung"
          >
            Andere Fokustypen erkunden
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 font-semibold px-4 py-2 text-sm hover:border-slate-400"
            data-testid="pro-analysis-result-restart-btn"
            title="Setzt deine Antworten zurück und startet den 20-Fragen-Test neu"
          >
            Analyse neu machen
          </button>
        </div>
      </div>
    </div>
  );
}
